import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Chip, useMediaQuery, IconButton, Tooltip } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  ContentWrapper,
  TypedText,
  StyledForm,
  WaitingMessage,
} from '../HomeInputStyles';

// Componentes reutilizables
import InputField from '../components/InputField';
import CustomizeButton from '../components/CustomizeButton';
import SlidingPromptCards from '../components/SlidingPromptCards';

// Variantes de animación tipo macOS
const containerVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.46, 0.45, 0.94],
      staggerChildren: 0.08
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: 15
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  }
};

const chipVariants = {
  hidden: {
    opacity: 0,
    scale: 0.9
  },
  visible: (index) => ({
    opacity: 1,
    scale: 1,
    transition: {
      delay: index * 0.05,
      duration: 0.3,
      ease: [0.25, 0.46, 0.45, 0.94]
    }
  })
};

// Estilos específicos para AssistantLayout
const SuggestionContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '800px',
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '10px',
  marginTop: '20px',
  margin: '20px auto 0',
  [theme.breakpoints.down('sm')]: {
    display: 'none', // Hide on mobile when using sliding cards
  }
}));

const SuggestionChip = styled(Chip)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    borderRadius: '20px',
    padding: '0 10px',
    height: '36px',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F7'),
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    boxShadow: isWallpaperMode ? '0 2px 5px rgba(0,0,0,0.1)' : 'none',
    '& .MuiChip-label': {
      fontSize: '14px',
      fontWeight: 500,
    },
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.5)'
        : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#E9E9EB'),
    },
  };
});

const RefreshButton = styled(IconButton)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#F2F2F7'),
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    boxShadow: isWallpaperMode ? '0 2px 5px rgba(0,0,0,0.1)' : 'none',
    marginLeft: '10px',
    '&:hover': {
      backgroundColor: isWallpaperMode
        ? 'rgba(128, 128, 128, 0.5)'
        : (theme.palette.mode === 'dark' ? '#2C2C2E' : '#E9E9EB'),
    },
  };
});

// Mejorado: Definir correctamente qué sugerencias vienen del namespace 'suggestions' y cuáles de 'prompts'
const suggestionNamespaces = {
  tech: 'suggestions',
  productivity: 'suggestions',
  quantum: 'suggestions',
  poem: 'suggestions',
  spanish: 'suggestions',
  coding: 'suggestions',
  // El resto son prompts
  story: 'prompts',
  vacation: 'prompts',
  workout: 'prompts',
  movie: 'prompts',
  blockchain: 'prompts',
  coverletter: 'prompts',
  mealplan: 'prompts',
  math: 'prompts',
  businessplan: 'prompts',
  summarize: 'prompts',
  gifts: 'prompts',
  email: 'prompts',
  climate: 'prompts',
  study: 'prompts',
  marketing: 'prompts',
  french: 'prompts',
  resignation: 'prompts',
  birthday: 'prompts',
  budget: 'prompts',
  review: 'prompts',
  interview: 'prompts',
  travel: 'prompts',
  gardening: 'prompts',
  persuasive: 'prompts',
  socialmedia: 'prompts',
  decoration: 'prompts',
  books: 'prompts',
  thankyou: 'prompts',
  ai: 'prompts',
  fitness: 'prompts',
  job: 'prompts',
  speech: 'prompts',
  wedding: 'prompts',
  quiz: 'prompts',
  timemanagement: 'prompts',
  productdesc: 'prompts',
  lessonplan: 'prompts',
  photography: 'prompts'
};

// Lista de todas las claves de sugerencias
const allSuggestionKeys = Object.keys(suggestionNamespaces);

const AssistantLayout = ({
  state,
  isWaiting,
  inputRef,
  handleMessageSubmit,
  updateState,
  debouncedUpdateSuggestions,
  waitingMessage,
  AGT_CARDS,
  PROMPT_CARDS,
  randomPromptCards,
  handleCardClick,
  isPro,
  selectedCard,
  handleCustomizeClick,
  interfaceStyle,
  t
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Si no recibimos t como prop, usamos el hook
  const translation = useTranslation();
  const { t: tFunc } = translation;
  const translate = t || tFunc;
  
  // Estado para las sugerencias actuales (para poder refrescarlas)
  const [currentSuggestions, setCurrentSuggestions] = useState(() => {
    // Seleccionar 6 sugerencias aleatorias para mostrar inicialmente
    return [...allSuggestionKeys].sort(() => 0.5 - Math.random()).slice(0, 6);
  });
  
  // Función para refrescar las sugerencias
  const refreshSuggestions = () => {
    setCurrentSuggestions([...allSuggestionKeys].sort(() => 0.5 - Math.random()).slice(0, 6));
  };
  
  // IMPORTANTE: Usar useMemo para que las tarjetas no se regeneren en cada render
  const slidingCards = useMemo(() => {
    // Combine all cards for the sliding view - including all PROMPT_CARDS for more variety
    return [...randomPromptCards, ...PROMPT_CARDS];
  }, [randomPromptCards, PROMPT_CARDS]); // Solo depende de arrays que son fijos

  // Función auxiliar para obtener la traducción correcta según el namespace
  const getTranslatedSuggestion = (key) => {
    const namespace = suggestionNamespaces[key] || 'prompts'; // Por defecto usar 'prompts'
    return translate(`${namespace}.${key}`);
  };

  return (
    <ContentWrapper
      component={motion.div}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        margin: '0 auto',
        width: '100%',
        maxWidth: isMobile ? 'none' : '1200px',
        overflow: 'visible',
        padding: isMobile ? 0 : undefined
      }}
    >
      <TypedText
        component={motion.h2}
        variants={itemVariants}
        variant="h2"
        gutterBottom
        sx={{
          textAlign: 'center !important',
          width: '100%',
          margin: '0 auto',
          marginBottom: '15px'
        }}
      >
        {translate('homeInput.heading')}
      </TypedText>

      <Box
        component={motion.div}
        variants={itemVariants}
        sx={{ width: '100%', maxWidth: '800px', margin: '0 auto' }}
      >
        <StyledForm onSubmit={handleMessageSubmit}>
          <InputField
            state={state}
            isWaiting={isWaiting}
            inputRef={inputRef}
            onSubmit={handleMessageSubmit}
            onStateUpdate={updateState}
            onSuggestionsUpdate={debouncedUpdateSuggestions}
            interfaceStyle={interfaceStyle}
            onSuggestionClick={handleCardClick}
          />
        </StyledForm>
      </Box>

      <AnimatePresence mode="wait">
        {isWaiting && (
          <motion.div
            key="waiting"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
          >
            <WaitingMessage>
              {waitingMessage}...
            </WaitingMessage>
          </motion.div>
        )}
      </AnimatePresence>

      {!isWaiting && (
        <>
          {/* Show sliding cards on mobile */}
          {isMobile ? (
            <Box
              component={motion.div}
              variants={itemVariants}
              sx={{ width: '100%', overflow: 'visible', padding: 0, margin: 0 }}
            >
              <SlidingPromptCards
                cards={slidingCards}
                handleCardClick={handleCardClick}
                isPro={isPro}
                selectedCard={selectedCard}
                interfaceStyle={interfaceStyle}
              />
            </Box>
          ) : (
            <Box
              component={motion.div}
              variants={itemVariants}
              display="flex"
              flexDirection="column"
              alignItems="center"
              width="100%"
            >
              <Box display="flex" alignItems="center" justifyContent="center">
                <SuggestionContainer>
                  {currentSuggestions.map((suggestionKey, index) => {
                    const translatedText = getTranslatedSuggestion(suggestionKey);
                    return (
                      <motion.div
                        key={`${suggestionKey}-${index}`}
                        custom={index}
                        variants={chipVariants}
                        initial="hidden"
                        animate="visible"
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <SuggestionChip
                          label={translatedText}
                          onClick={() => handleCardClick(translatedText)}
                          clickable
                        />
                      </motion.div>
                    );
                  })}
                </SuggestionContainer>
                <Tooltip title={translate('homeInput.discoverPrompts')}>
                  <motion.div
                    whileHover={{ scale: 1.1, rotate: 180 }}
                    whileTap={{ scale: 0.9 }}
                    transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                  >
                    <RefreshButton onClick={refreshSuggestions} size="medium">
                      <RefreshCw
                        size={18}
                        color={theme.palette.actualMode === 'wallpaper'
                          ? '#FFFFFF'
                          : theme.palette.mode === 'dark' ? '#0385FF' : '#007BFE'
                        }
                      />
                    </RefreshButton>
                  </motion.div>
                </Tooltip>
              </Box>
            </Box>
          )}

          <Box
            component={motion.div}
            variants={itemVariants}
            display="flex"
            justifyContent="center"
            width="100%"
            mt={isMobile ? 1 : 3}
          >
            <CustomizeButton onClick={handleCustomizeClick} />
          </Box>
        </>
      )}
    </ContentWrapper>
  );
};

AssistantLayout.propTypes = {
  state: PropTypes.object.isRequired,
  isWaiting: PropTypes.bool.isRequired,
  inputRef: PropTypes.object.isRequired,
  handleMessageSubmit: PropTypes.func.isRequired,
  updateState: PropTypes.func.isRequired,
  debouncedUpdateSuggestions: PropTypes.func.isRequired,
  waitingMessage: PropTypes.string.isRequired,
  AGT_CARDS: PropTypes.array.isRequired,
  PROMPT_CARDS: PropTypes.array.isRequired,
  randomPromptCards: PropTypes.array.isRequired,
  handleCardClick: PropTypes.func.isRequired,
  isPro: PropTypes.bool,
  selectedCard: PropTypes.string,
  handleCustomizeClick: PropTypes.func.isRequired,
  interfaceStyle: PropTypes.string.isRequired,
  t: PropTypes.func,
  onSuggestionClick: PropTypes.func // ✅ AÑADIR ESTA LÍNEA SI NO ESTÁ
};

export default AssistantLayout;